/**
 * Permission registry — single source of truth shared by the API
 * (Zod validation, audit narration, future enforcement middleware) and the
 * web app (permissions editor).
 *
 * A role owns an array of permission entries. Each entry has a `permission`
 * id from a registry, plus a `scope` if the registry entry declares scope
 * options. Scope is omitted entirely for global permissions
 * (e.g. `system_role.view`).
 *
 * The registry is parameterized so future use cases (API key scopes,
 * service-account permissions, …) can supply their own registry and reuse
 * the validator + editor + audit machinery without duplication.
 */
import type { FieldError } from './errors';

export const PERMISSION_SCOPES = [
  'all',
  'my_customers',
  'my_organization',
  'my_organization_my_customers',
  // Lead-based scopes (Report group today). `my_teams` is the customers the
  // caller is an active team LEAD of — distinct from `my_customers` (mere
  // membership). `my_organization_my_teams` is the union with the HR subtree.
  'my_teams',
  'my_organization_my_teams',
] as const;
export type PermissionScope = (typeof PERMISSION_SCOPES)[number];

/**
 * Default display label per scope code, shared across permission groups.
 *
 * A few groups override an individual label — Employee shows "My team" for
 * `my_organization` because it returns employees (the caller's HR reports),
 * not customers. Resolve through {@link scopeLabel} so those overrides apply;
 * this map is the fallback.
 */
export const SCOPE_LABELS: Record<PermissionScope, string> = {
  all: 'All',
  my_customers: 'My customers',
  my_organization: 'My organization',
  my_organization_my_customers: 'My organization + My customers',
  my_teams: 'My team',
  my_organization_my_teams: 'My organization + My team',
};

/**
 * Per-group label overrides. The scope codes mean the same thing everywhere,
 * but the natural-language label differs by entity: `my_organization` is "My
 * organization['s customers]" on the Customer/Timesheet rows but "My team" on
 * the Employee rows, since for employees it resolves to the caller's reporting
 * subtree rather than customers.
 *
 * Keyed by the group's display string, which is also its identity in the
 * registry. A `permissions.test.ts` invariant asserts every key here is a real
 * group offering the overridden scope, so a future group rename fails CI loudly
 * instead of silently dropping the override back to {@link SCOPE_LABELS}.
 */
export const GROUP_SCOPE_LABELS: Record<string, Partial<Record<PermissionScope, string>>> = {
  Employee: { my_organization: 'My team' },
};

/** Display label for a scope code within a permission group. Falls back to the
 *  shared {@link SCOPE_LABELS} when the group has no override. */
export function scopeLabel(group: string, scope: PermissionScope): string {
  return GROUP_SCOPE_LABELS[group]?.[scope] ?? SCOPE_LABELS[scope];
}

/**
 * Tier controls who can grant a permission.
 *
 * - `tenant` (default): grantable by a tenant admin to any role inside their
 *   own tenant. The default for resources owned by the tenant.
 * - `platform`: too sensitive for tenant admins to hand out — these are
 *   granted only by platform operators via SQL or a future admin tool, and
 *   are rejected if they appear in a tenant-context grant request.
 *
 * Currently platform-tier: `tenant.*` only (the "edit your own tenant"
 * surface — domains[] in particular reroutes SSO across tenants). `audit.view`
 * is tenant-tier: audit data is tenant-scoped, so each tenant's admin may hold
 * it; the per-entity `.view` remains the fine-grained gate on which rows show.
 */
export type PermissionTier = 'tenant' | 'platform';

export interface PermissionDef {
  /** Dotted id stored in the DB (e.g., 'customer.sow.edit'). */
  readonly id: string;
  /** Resource group used to lay out the editor. */
  readonly group: string;
  /** Action label within the group. */
  readonly label: string;
  /**
   * Allowed scopes for this permission. Empty array = global, scope must be
   * omitted. Otherwise scope is required and must be one of these values.
   */
  readonly scopes: readonly PermissionScope[];
  /** Who can grant this permission. Defaults to 'tenant' when omitted. */
  readonly tier?: PermissionTier;
  /**
   * Marker for permissions registered ahead of their endpoint. Excluded
   * from the route-coverage CI check; otherwise behaves like any other id.
   */
  readonly pending?: boolean;
  /**
   * Three-way tier ladder:
   *   - default (no flag)    → tenant-grantable, on Administrator + Super Admin
   *   - `restricted: true`   → tenant-grantable, off Administrator, on Super Admin
   *   - `tier: 'platform'`   → NOT tenant-grantable, off both Admin and Super Admin
   *
   * `restricted` is for capabilities a tenant admin should be aware of but
   * not the default grant — today just `user.impersonate`.
   */
  readonly restricted?: boolean;
}

// Order here drives the UI rendering order. Within a group, the action order
// (View, Edit, …) is also preserved.
export const SYSTEM_ROLE_PERMISSIONS = [
  // ---- Employee ---------------------------------------------------------
  // Scope `my_organization` resolves to the caller's recursive HR reporting
  // subtree (labelled "My team" on this group); `all` is unrestricted. There
  // is no customer-team scope here — employee visibility is HR-hierarchy based.
  { id: 'user.view', group: 'Employee', label: 'View', scopes: ['all', 'my_organization'] },
  { id: 'user.edit', group: 'Employee', label: 'Edit', scopes: ['all', 'my_organization'] },
  // Admin-tier employee controls layered on top of `user.edit`, mirroring
  // `customer.admin`. Today it gates the historical amend (D10): editing what a
  // PAST version of the record says, which is a different authority from
  // editing the record — an ordinary edit changes it from today, an amend
  // changes what every report over that version's period has always said.
  // Presence-only; WHICH employees are reachable still derives from the
  // caller's `user.view` scope, because the amend runs `getById` first.
  { id: 'user.admin', group: 'Employee', label: 'Admin', scopes: [] },
  // Assume another user's identity for testing / support. The session
  // stays on the real human; an `impersonated_ba_user_id` column on
  // `ba_session` rides on top. Every audit row records the real human
  // as actor + the impersonated user separately. Single-level only —
  // an impersonator can't chain into another impersonation.
  //
  // `restricted: true` keeps it OFF the regular Administrator role; only
  // the Super Admin role (seeded alongside Administrator on every new
  // tenant) gets it by default.
  { id: 'user.impersonate', group: 'Employee', label: 'Impersonate', scopes: [], restricted: true },

  // ---- User Cost --------------------------------------------------------
  // Compensation data on a user's record — separated from the broader
  // user.view / user.edit gate because cost is commercially sensitive
  // and not every viewer should see payroll-equivalent figures via the
  // export or the cost history table. The Employee Cost column in
  // GET /api/users/export is omitted when the caller lacks user.cost.view;
  // GET/POST /api/user-employee-costs gate on these directly.
  //
  // Granted on Administrator + Super Admin by default (the bootstrap
  // seed pulls from FULL_REGISTRY_GRANT). For other roles, an admin
  // must explicitly grant.
  {
    id: 'user.cost.view',
    group: 'Employee',
    label: 'View cost',
    scopes: ['all', 'my_organization'],
  },
  {
    id: 'user.cost.edit',
    group: 'Employee',
    label: 'Edit cost',
    scopes: ['all', 'my_organization'],
  },

  // ---- Employee PII -----------------------------------------------------
  // An employee's home address (address lines, city, state, zip, country)
  // and free-text location label — personally identifying data separated
  // from the broader user.view gate because not every viewer should see it.
  //
  // Presence-only (no scope): the address columns are redacted server-side
  // from the detail, list, and export payloads for callers without it, and
  // the detail page renders a locked placeholder in place of the value. A
  // caller ALWAYS sees their OWN address regardless of the permission.
  //
  // Granted on Administrator + Super Admin by default (the bootstrap seed
  // pulls from FULL_REGISTRY_GRANT). For other roles, an admin must grant.
  { id: 'user.pii.view', group: 'Employee', label: 'View PII (address)', scopes: [] },

  // ---- Customer ---------------------------------------------------------
  {
    id: 'customer.view',
    group: 'Customer',
    label: 'View',
    scopes: ['all', 'my_customers', 'my_organization', 'my_organization_my_customers'],
  },
  {
    id: 'customer.edit',
    group: 'Customer',
    label: 'Edit',
    scopes: ['all', 'my_customers', 'my_organization', 'my_organization_my_customers'],
  },
  // Admin-only customer controls layered on top of `customer.edit`. Today it
  // gates the "internal customer" designation (seeing + toggling the flag) —
  // a tenant-wide structural setting, not a per-record edit. Enforced in the
  // service via `assertPermission` (no dedicated route), mirrored in the UI.
  // It also gates the historical amend (D10) — see `user.admin` for why that is
  // an admin-tier authority rather than part of `customer.edit`.
  { id: 'customer.admin', group: 'Customer', label: 'Admin', scopes: [] },

  // ---- Customer SOW -----------------------------------------------------
  // Presence-only: which customers' SOWs a caller may see/edit derives from
  // the caller's `customer.view` / `customer.edit` scope (a SOW is a child of
  // a customer, reachable only through it). See docs/permissions.md.
  { id: 'customer.sow.view', group: 'Customer Engagement', label: 'View', scopes: [] },
  { id: 'customer.sow.edit', group: 'Customer Engagement', label: 'Edit', scopes: [] },

  // ---- Customer Contact -------------------------------------------------
  // Gates the per-customer contacts tab (billing / alternate contact people).
  // Presence-only — which customers derives from `customer.view`/`customer.edit`.
  { id: 'customer.contact.view', group: 'Customer Contact', label: 'View', scopes: [] },
  { id: 'customer.contact.edit', group: 'Customer Contact', label: 'Edit', scopes: [] },

  // ---- Ratecard ---------------------------------------------------------
  // Tenant-singleton T&M rate card. Read gates the Setup → Ratecard page
  // (including past/future as-of views); edit gates creating the initial
  // card and amending it (each amendment is a new version).
  { id: 'ratecard.view', group: 'Ratecard', label: 'View', scopes: [] },
  { id: 'ratecard.edit', group: 'Ratecard', label: 'Edit', scopes: [] },

  // ---- Expense Catalog --------------------------------------------------
  // Gates the Setup → Expense Catalog page: the tenant's master list of
  // pass-through items (software seats, subscriptions, filing fees…) that can
  // later be associated with customers and billed on. Tenant-wide reference
  // data, not per-customer, so no scopes — a holder sees/edits the whole
  // catalog. `.edit` additionally gates import.
  //
  // Entity-named (`expensecatalog.*`), like `ratecard.*`, rather than carrying
  // an invoicing prefix. The catalog is tenant-wide reference data in its own
  // right — it lives under /setup and is edited by people who never open an
  // invoice — so naming it after the module it feeds would overstate the tie.
  // Auto-grants to the seeded Administrator (FULL_REGISTRY_GRANT) and Super Admin
  // (SUPER_ADMIN_GRANT); the PR's Migration section backfills both ids onto
  // those roles for existing tenants.
  { id: 'expensecatalog.view', group: 'Expense Catalog', label: 'View', scopes: [] },
  { id: 'expensecatalog.edit', group: 'Expense Catalog', label: 'Edit', scopes: [] },

  // ---- Invoice Settings -------------------------------------------------
  // The tenant's issuer block (name, address, phone, website), the remittance
  // instructions printed on every invoice, and the defaults a new invoice
  // inherits (terms, due-day offset, currency, numbering start, retainer
  // invoice mode). Tenant-wide reference data, so no scopes.
  //
  // Split out from `invoice.*` on purpose. The payment-details block is a
  // bank account number, an ABA routing number and a Zelle address; the set
  // of people who need to read an invoice is far wider than the set who
  // should see — let alone change — where the money lands. Anyone who can
  // edit these can silently redirect every future payment, which makes this
  // the most sensitive pair in the `invoice.*` family.
  //
  // Auto-grants to the seeded Administrator (FULL_REGISTRY_GRANT) and Super
  // Admin (SUPER_ADMIN_GRANT), which covers NEW tenants only; the PR's
  // Migration section backfills both ids onto those roles for existing ones.
  { id: 'invoice.settings.view', group: 'Invoice Settings', label: 'View', scopes: [] },
  { id: 'invoice.settings.edit', group: 'Invoice Settings', label: 'Edit', scopes: [] },

  // ---- Invoice ----------------------------------------------------------
  // The invoice list, the editor, and the two state transitions.
  //
  // Presence-only, like the other per-customer surfaces: WHICH customers'
  // invoices a caller may see or edit derives from their `customer.view` /
  // `customer.edit` scope, enforced in `InvoiceService` via
  // `CustomerAccessService`. Declaring scopes here as well would be a second
  // source of truth that can contradict the first.
  //
  // Four ids rather than the usual two, because these are four genuinely
  // different authorities:
  //   - `.edit` fixes a typo in a draft.
  //   - `.approve` is a financial sign-off — it says an amount is correct and
  //     ready to be billed, and it is what the bulk-approve queue gates on.
  //   - `.send` puts a document in a customer's inbox. Irreversible in the way
  //     that matters: you cannot unsend it, and `sent` is terminal.
  // Someone who can correct a draft should not thereby be able to bill a
  // customer, and someone who approves amounts is not necessarily the person
  // who mails them.
  //
  // All four auto-grant to the seeded Administrator (FULL_REGISTRY_GRANT) and
  // Super Admin (SUPER_ADMIN_GRANT), which covers NEW tenants only; the PR's
  // Migration section backfills them onto those roles for existing ones.
  { id: 'invoice.view', group: 'Invoice', label: 'View', scopes: [] },
  { id: 'invoice.edit', group: 'Invoice', label: 'Edit', scopes: [] },
  { id: 'invoice.approve', group: 'Invoice', label: 'Approve', scopes: [] },
  { id: 'invoice.send', group: 'Invoice', label: 'Send', scopes: [] },

  // ---- Customer Team ----------------------------------------------------
  // Gates the per-customer team-membership tab. Presence-only — which
  // customers' teams a caller may see/edit derives from the caller's
  // `customer.view` / `customer.edit` scope. (Billing rate is no longer a
  // membership attribute, so there is no separate rate-visibility gate.)
  { id: 'customer.team.view', group: 'Customer Team', label: 'View', scopes: [] },
  { id: 'customer.team.edit', group: 'Customer Team', label: 'Edit', scopes: [] },

  // ---- Customer Budget --------------------------------------------------
  // Gates the per-customer budgets tab (hours allowed per service role per
  // period) and the over-budget figures shown during timesheet entry and
  // approval. Presence-only — which customers' budgets a caller may see/edit
  // derives from the caller's `customer.view` / `customer.edit` scope.
  //
  // `.view` is a genuine data gate, not just a screen gate: without it a user
  // still learns that an entry is over budget (they have to, or they can't
  // act on it) but never sees the pool size or the hours consumed. The API
  // scrubs those figures rather than the UI hiding them.
  { id: 'customer.budget.view', group: 'Customer Budget', label: 'View', scopes: [] },
  { id: 'customer.budget.edit', group: 'Customer Budget', label: 'Edit', scopes: [] },

  // ---- Customer Expense -------------------------------------------------
  // Gates the pass-through pane on the per-customer Invoicing tab — the lines
  // that seed each generated invoice with the items this customer is billed
  // on for. Presence-only — which customers' lines a caller may see/edit
  // derives from the caller's `customer.view` / `customer.edit` scope, the
  // same pattern as `customer.budget.*` and `customer.team.*`.
  //
  // Deliberately `customer.expense.*` rather than an invoicing-prefixed id:
  // a module prefix marks TENANT-wide reference data a holder sees in full,
  // whereas these ids are customer-scoped and read their scope off
  // `customer.*`. Grouping them with the other per-customer tabs is what makes
  // the role editor legible.
  { id: 'customer.expense.view', group: 'Customer Expense', label: 'View', scopes: [] },
  { id: 'customer.expense.edit', group: 'Customer Expense', label: 'Edit', scopes: [] },

  // ---- Timesheet --------------------------------------------------------
  {
    id: 'timesheet.view',
    group: 'Timesheet',
    label: 'View',
    scopes: ['all', 'my_customers', 'my_organization'],
  },
  {
    id: 'timesheet.edit',
    group: 'Timesheet',
    label: 'Edit',
    scopes: ['all', 'my_customers', 'my_organization'],
  },
  {
    id: 'timesheet.submit_for',
    group: 'Timesheet',
    label: 'Submit for',
    scopes: ['all', 'my_customers', 'my_organization'],
  },
  // Gates the /approvals page and the underlying
  // /api/timesheet-approvals/* routes. The data layer still scopes to
  // "rows where the caller is a pending approver" — this permission
  // controls who sees the surface at all (so non-approvers don't get a
  // sidebar item that always reads empty). Decision authorization
  // continues to come from `approvers` membership at the service layer.
  {
    id: 'timesheet.approve',
    group: 'Timesheet',
    label: 'Approve',
    scopes: ['all', 'my_customers', 'my_organization'],
  },
  // Gates updating the tenant-wide timesheet lock settings
  // (PUT /api/timesheet-settings). Reading the settings only needs
  // `timesheet.view` so the timesheet grid can surface the next lock
  // date; mutating them is reserved for this permission.
  {
    id: 'timesheet.settings',
    group: 'Timesheet',
    label: 'Settings',
    scopes: [],
  },
  // Timesheet administration. Formerly gated the manual
  // POST /api/timesheets/reminders/send trigger, which has been removed now
  // that reminders are sent entirely by the automated cron fanout (which
  // does NOT consult this permission). Retained as a reserved grant for
  // future tenant-wide timesheet admin actions; currently gates nothing.
  {
    id: 'timesheet.admin',
    group: 'Timesheet',
    label: 'Admin',
    scopes: [],
  },

  // ---- Timer ------------------------------------------------------------
  // In-progress time tracking. Timers are personal — the service rejects
  // mutations of someone else's timer regardless of permission, so these
  // are effectively per-user toggles for whether the feature is available.
  { id: 'timer.view', group: 'Timer', label: 'View', scopes: [] },
  { id: 'timer.edit', group: 'Timer', label: 'Edit', scopes: [] },

  // ---- System Role ------------------------------------------------------
  { id: 'system_role.view', group: 'System Role', label: 'View', scopes: [] },
  { id: 'system_role.edit', group: 'System Role', label: 'Edit', scopes: [] },

  // ---- Service Role -----------------------------------------------------
  { id: 'service_role.view', group: 'Service Role', label: 'View', scopes: [] },
  { id: 'service_role.edit', group: 'Service Role', label: 'Edit', scopes: [] },

  // ---- Department -------------------------------------------------------
  { id: 'department.view', group: 'Department', label: 'View', scopes: [] },
  { id: 'department.edit', group: 'Department', label: 'Edit', scopes: [] },

  // ---- Activity ----------------------------------------------------------
  { id: 'activity.view', group: 'Activity', label: 'View', scopes: [] },
  { id: 'activity.edit', group: 'Activity', label: 'Edit', scopes: [] },

  // ---- Skills -----------------------------------------------------------
  { id: 'skills.view', group: 'Skills', label: 'View', scopes: [] },
  { id: 'skills.edit', group: 'Skills', label: 'Edit', scopes: [] },

  // ---- Announcement -------------------------------------------------------
  // Gates the Setup → Announcements admin screen (manage site-wide banners).
  // Tenant-wide broadcast content, not per-user, so no scopes.
  { id: 'announcement.view', group: 'Announcement', label: 'View', scopes: [] },
  { id: 'announcement.edit', group: 'Announcement', label: 'Edit', scopes: [] },
  // No id for reading the live-banner feed: every signed-in user reads it,
  // ungated (`GET /api/announcements/live` sits behind the session guard
  // only). A permission for it meant every new custom role silently lost the
  // banners until someone remembered to tick it.

  // ---- Release Note -------------------------------------------------------
  // Reading What's New is ungated for every signed-in user, for the same
  // reason as the live-banner feed above — so there is no `.view` id. Only
  // `.edit` (add/edit/delete/restore, and the audit trail) is permissioned.
  { id: 'release_note.edit', group: 'Release Note', label: 'Edit', scopes: [] },

  // ---- Holiday ----------------------------------------------------------
  // Gates the Setup → Holidays calendar: the per-country list of non-working
  // dates. Tenant-wide reference data (not per-user), so no scopes — a holder
  // sees/edits the whole tenant's calendar. Auto-grants to the seeded
  // Administrator (FULL_REGISTRY_GRANT) and Super Admin (SUPER_ADMIN_GRANT);
  // the PR's Migration section backfills both ids onto those roles for
  // existing tenants. The calendar is read-only reference data today; the
  // working-days-per-month calculation that consumes it lands separately.
  { id: 'holiday.view', group: 'Holiday', label: 'View', scopes: [] },
  { id: 'holiday.edit', group: 'Holiday', label: 'Edit', scopes: [] },

  // ---- Chart of Accounts ------------------------------------------------
  // Gates Setup → Chart of accounts: the read-only list of income accounts
  // invoice lines are booked to. View only — the chart is maintained in the
  // books, never edited in the app. Tenant-wide reference data, so no scopes.
  // The list endpoint ALSO answers to invoice.view, invoice.settings.view and
  // activity.view, because the editor, the settings page and the activity
  // form all need its pickers. Auto-grants to the seeded Administrator and
  // Super Admin for new tenants; the PR's Migration section backfills existing
  // ones.
  { id: 'chart_of_accounts.view', group: 'Chart of Accounts', label: 'View', scopes: [] },

  // ---- Custom Fields ----------------------------------------------------
  // Gates definition management in Setup. Reading and writing custom-field
  // *values* on host entities flows through the host's own permission
  // (user.view/edit, customer.view/edit, customer.sow.view/edit).
  { id: 'custom_fields.view', group: 'Custom Fields', label: 'View', scopes: [] },
  { id: 'custom_fields.edit', group: 'Custom Fields', label: 'Edit', scopes: [] },

  // ---- Approval Policy --------------------------------------------------
  // Gates the admin UI under /approval-policies. Decision authorization
  // (approve/reject on an instance) is NOT permission-driven — it comes
  // from membership in the `approvers` table for the active step. See
  // typescript/docs/approvals.md.
  { id: 'approval_policy.view', group: 'Approval Policy', label: 'View', scopes: [] },
  { id: 'approval_policy.edit', group: 'Approval Policy', label: 'Edit', scopes: [] },

  // ---- Company ----------------------------------------------------------
  // Gates the Setup → Company page: the read-only company profile
  // (name + email domains, read via GET /api/company) and the login-history
  // CSV export (GET /api/company/login-history/export). Tenant-tier — company
  // data is tenant-scoped, so each tenant's admin may hold it. Auto-grants to
  // the seeded Administrator (FULL_REGISTRY_GRANT) and Super Admin
  // (SUPER_ADMIN_GRANT); the PR's Migration section backfills it onto those
  // two roles for existing tenants. Distinct from the platform-tier `tenant.*`
  // pair below, which is NOT tenant-grantable.
  { id: 'company.view', group: 'Company', label: 'View', scopes: [] },

  // ---- Tenant — platform-tier --------------------------------------------
  // `tenant.edit` lets an admin rename their own tenant. The route layer
  // refuses to touch `domains[]` (which would re-route SSO across tenants).
  // Platform-tier keeps it out of the tenant role editor entirely.
  { id: 'tenant.view', group: 'Tenant', label: 'View', scopes: [], tier: 'platform' },
  { id: 'tenant.edit', group: 'Tenant', label: 'Edit', scopes: [], tier: 'platform' },

  // ---- Audit ------------------------------------------------------------
  // Gates the platform-wide Audit Log screen + every /api/audit read. It is
  // the "may you call /api/audit at all" gate; the entity's own `.view` is
  // still required per row, so audit.view never widens what fields a caller
  // can see — when listing across types the result is restricted to the
  // entity types the caller already has `.view` on. Tenant-tier: each tenant
  // sees only its own tenant-scoped audit trail, so it auto-grants to the
  // seeded Administrator (FULL_REGISTRY_GRANT) and Super Admin
  // (SUPER_ADMIN_GRANT); the PR's Migration section backfills it onto those
  // two roles for existing tenants.
  { id: 'audit.view', group: 'Audit', label: 'View', scopes: [] },

  // ---- Notifications ----------------------------------------------------
  // .view gates the inbox + templates list; .edit gates updating personal
  // delivery preferences. Routes live in apps/notifications and are
  // mounted under /api/notifications/* — both call requirePermission().
  { id: 'notifications.view', group: 'Notifications', label: 'View', scopes: [] },
  { id: 'notifications.edit', group: 'Notifications', label: 'Edit', scopes: [] },

  // ---- Reports ----------------------------------------------------------
  // Gates the Reports section (sidebar item + /api/reports/* routes). The
  // first report is the cross-domain Timesheet report, which reads timesheet
  // entries joined to employee, customer, product, service-role and approval
  // data. The scope restricts which rows the report returns (enforced
  // server-side in the report query, so preview + both export paths share one
  // chokepoint — see core/reports/access.ts):
  //   - `all`                      → every row in the tenant (unrestricted).
  //   - `my_organization`          → rows logged by the caller's HR subtree,
  //                                  plus the caller's own.
  //   - `my_teams`                 → rows on the customers the caller is an
  //                                  active team LEAD of (date-aware).
  //   - `my_organization_my_teams` → the union of the two above.
  // Auto-grants `all` to the seeded Administrator (FULL_REGISTRY_GRANT) and
  // Super Admin (SUPER_ADMIN_GRANT) roles for new tenants; the PR's Migration
  // section backfills `scope: 'all'` onto existing role rows.
  {
    id: 'report.view',
    group: 'Report',
    label: 'View',
    scopes: ['all', 'my_organization', 'my_teams', 'my_organization_my_teams'],
  },

  // KEEPING a report: the builder page (/reports/new) and every write on
  // /api/report-definitions. Held IN ADDITION to `report.view`, never instead
  // of it — `report.view`'s scope still decides which rows any report returns.
  //
  // NOT running one. The analytics endpoints ask only for `report.view`,
  // because someone holding it alone must be able to open a report shared with
  // them, change its filters and dates while looking at it, and export
  // (CR-1.5). This id is the line between running and saving, and it used to
  // sit one step too far left.
  //
  // `edit` rather than a verb of its own, even though nothing is saved yet:
  // `edit` already means create-and-update everywhere in this registry (there
  // is no `create` anywhere), and when `report_definitions` lands, saving a
  // report is this same id. A separate `report.build` would leave two ids over
  // one capability and no answer to "may I edit a saved report without being
  // able to build one". The second axis, when it comes, is SHARING.
  //
  // Scopeless. Two scoped permissions over the same rows would be two answers
  // to "what may this caller see", and the reports read the one the service
  // passes down (`scopeOf(ctx, 'report.view')`). A scope here would be
  // silently ignored, which is worse than not offering one.
  { id: 'report.edit', group: 'Report', label: 'Edit', scopes: [] },

  // The second axis the note above promised: giving OTHER PEOPLE a report you
  // saved. One verb whether the recipient is one person, a role, or everyone
  // (CR-1.3) — the three differ only in who is named, and splitting them would
  // be three ids over one decision.
  //
  // What it grants is the right to hand over a DEFINITION, never a result set:
  // a recipient runs the report themselves, under their own `report.view`
  // scope, so sharing can never widen what anybody can see (CR-3.1). That is
  // why this is scopeless too — there is no disclosure here for a scope to
  // narrow.
  //
  // It also gates the compiled-SQL disclosure on `POST /api/analytics/query`.
  // The statement names the warehouse's tables and columns, which is a
  // different kind of thing from the rows a report returns, and this is the
  // widest grant in the group — so requiring it ON TOP of `report.edit` keeps
  // "may compose a report" and "may read how a report is compiled" as separate
  // answers.
  { id: 'report.share', group: 'Report', label: 'Share', scopes: [] },

  // STANDARD LABELS: the company-wide ones on the Reports page (Budgets vs
  // Actuals, Utilization, Timesheet…) — creating, renaming and deleting them,
  // and putting a report under one. Personal labels and stars need nothing
  // beyond `report.view`: they organise one person's own list and nobody else
  // ever sees them.
  //
  // A label NEVER GRANTS ACCESS. A report sits under a standard label for the
  // people it is already shared with and for nobody else, so this id decides
  // where a report is filed, not who may open it — that stays `report.share`'s
  // question alone. It is separate from `report.share` because the two are
  // different jobs: sharing hands over your own report, and this curates
  // everybody's list.
  //
  // Scopeless: there are no rows here for a scope to narrow.
  { id: 'report.label', group: 'Report', label: 'Manage standard labels', scopes: [] },

  // REACHING THE DASHBOARD SURFACE AT ALL: /dashboards in the sidebar, the
  // route behind it, and every read on /api/dashboards.
  //
  // A NARROWING GATE, NEVER A SUBSTITUTE. It is asked for IN ADDITION to
  // `report.view`, never instead of it: a tile's numbers come from
  // `POST /api/analytics/query`, which asks for `report.view`, and that is the
  // id carrying the row scope deciding what any of those numbers contain.
  // Holding this one alone opens nothing, so it can only ever subtract from
  // what `report.view` already allows — which is what keeps it from being a
  // second, contradictory answer to "what may this caller see".
  //
  // It exists because "may build reports" and "may see the Dashboards section"
  // turned out to be questions a tenant wants to answer separately. Until this
  // id there was no way to say no to the second without also saying no to the
  // first: the section was gated on `report.view`, the SAME id as Reports, so
  // every user who could open a report also had Dashboards in their sidebar.
  // The registry had no vocabulary for that, and an admin looking for the
  // switch could not find one because none existed.
  //
  // Scopeless, for the same reason `report.edit` is: two scoped permissions
  // over the same rows would be two answers to "what may this caller see", and
  // the tiles read `report.view`'s.
  { id: 'dashboard.view', group: 'Report', label: 'View dashboards', scopes: [] },

  // COMPOSING A DASHBOARD: every write on /api/dashboards. The exact twin of
  // `report.edit`, one artefact along — a dashboard is a saved arrangement of
  // saved reports, owned by one person and shared the same three ways.
  //
  // Held on top of `dashboard.view` (which is itself held on top of
  // `report.view`), so the three read in the role editor the way they compose:
  // see the section, then save into it.
  //
  // Scopeless, for the same reason `report.edit` is: two scoped permissions
  // over the same rows would be two answers to "what may this caller see", and
  // the tiles read `report.view`'s.
  { id: 'dashboard.edit', group: 'Report', label: 'Edit dashboards', scopes: [] },

  // Giving other people a dashboard you built. Separate from `report.share`
  // because the two hand over different things and a tenant may reasonably
  // want one without the other: sharing a dashboard publishes an ARRANGEMENT,
  // and its tiles keep pointing at reports whose own sharing has not changed —
  // a recipient who cannot reach a referenced report sees a tombstone in that
  // tile rather than its contents, which is what keeps this grant from being a
  // back door around `report.share`.
  { id: 'dashboard.share', group: 'Report', label: 'Share dashboards', scopes: [] },

  // ---- Feedback ---------------------------------------------------------
  // In-app "Send feedback" widget. Granted to every seeded role by default
  // (any authed user should be able to flag a bug or a suggestion); see
  // the Migration section of the PR description for the SQL to backfill
  // existing role rows.
  { id: 'feedback.send', group: 'Feedback', label: 'Send', scopes: [] },
] as const satisfies readonly PermissionDef[];

/** Literal union of every system-role permission id. Gives compile-time
 *  safety to code that names specific permissions (`requirePermission('customer.edit')`). */
export type SystemRolePermissionId = (typeof SYSTEM_ROLE_PERMISSIONS)[number]['id'];

/** Default registry used when callers don't supply one — covers system roles
 *  today. Future role-shaped consumers can pass their own. */
export const PERMISSION_REGISTRY: readonly PermissionDef[] = SYSTEM_ROLE_PERMISSIONS;

/**
 * The full registry expressed as a grantable `PermissionEntry[]`. Scoped
 * permissions default to `scope: 'all'` (the broadest grant); global
 * permissions omit `scope`. Used by tenant bootstrap to seed an
 * `Administrator` role that can immediately drive the system — and by the
 * "grant Admin everything" migration in the permissions PR.
 *
 * Updating the registry above auto-updates this constant; nothing to
 * synchronize.
 */
export const FULL_REGISTRY_GRANT: readonly PermissionEntry[] = (
  SYSTEM_ROLE_PERMISSIONS as readonly PermissionDef[]
)
  .filter((p) => !p.restricted)
  .map((p) =>
    p.scopes.length > 0 ? { permission: p.id, scope: 'all' as const } : { permission: p.id },
  );

/**
 * Super Admin grant — strict superset of `FULL_REGISTRY_GRANT`. Includes
 * every permission in the registry, restricted + platform-tier alike, so
 * Super Admin can never be less powerful than Administrator on any axis.
 *
 * Today the only addition over Admin is `user.impersonate` (the sole
 * `restricted` permission). Adding new permissions auto-extends this
 * constant; nothing else to synchronize.
 */
export const SUPER_ADMIN_GRANT: readonly PermissionEntry[] = (
  SYSTEM_ROLE_PERMISSIONS as readonly PermissionDef[]
).map((p) =>
  p.scopes.length > 0 ? { permission: p.id, scope: 'all' as const } : { permission: p.id },
);

export type PermissionRegistry = readonly PermissionDef[];

/** Build a `{id → def}` lookup once per registry. */
export function indexRegistry(registry: PermissionRegistry): ReadonlyMap<string, PermissionDef> {
  return new Map(registry.map((p) => [p.id, p]));
}

const DEFAULT_INDEX = indexRegistry(PERMISSION_REGISTRY);

export function lookupPermission(id: string, registry?: PermissionRegistry): PermissionDef | null {
  const index = registry ? indexRegistry(registry) : DEFAULT_INDEX;
  return index.get(id) ?? null;
}

export interface PermissionEntry {
  permission: string;
  scope?: PermissionScope;
}

/**
 * Validate a permissions array against a registry. Returns a tagged result
 * with per-index error paths so callers can surface inline.
 *
 * Errors carry the canonical `FieldErrorCode` vocabulary (`REQUIRED`,
 * `INVALID_ENUM_VALUE`, `UNIQUE_VIOLATION`, `INVALID_FORMAT`) — the service
 * can spread them straight into a `ValidationError.fields` without translating.
 *
 * `tier` defaults to `'tenant'` (the strict path used by HTTP routes that
 * are exposed to tenant admins). Pass `'platform'` to permit grants of
 * `tier: 'platform'` permission ids — used by bootstrap code that seeds
 * tenants with the full registry. NEVER use `'platform'` from an HTTP-
 * driven path.
 */
export function validatePermissions(
  entries: readonly unknown[],
  registry: PermissionRegistry = PERMISSION_REGISTRY,
  tier: PermissionTier = 'tenant',
): { ok: true; value: PermissionEntry[] } | { ok: false; errors: FieldError[] } {
  const index = registry === PERMISSION_REGISTRY ? DEFAULT_INDEX : indexRegistry(registry);
  const errors: FieldError[] = [];
  const seen = new Set<string>();
  const value: PermissionEntry[] = [];

  entries.forEach((raw, i) => {
    if (!raw || typeof raw !== 'object') {
      errors.push({
        path: `permissions.${i}`,
        code: 'INVALID_FORMAT',
        message: 'must be an object',
      });
      return;
    }
    const e = raw as Record<string, unknown>;
    const permission = typeof e.permission === 'string' ? e.permission : '';
    if (!permission) {
      errors.push({
        path: `permissions.${i}.permission`,
        code: 'REQUIRED',
        message: 'required',
      });
      return;
    }
    const def = index.get(permission);
    if (!def) {
      errors.push({
        path: `permissions.${i}.permission`,
        code: 'INVALID_ENUM_VALUE',
        message: `unknown permission "${permission}"`,
      });
      return;
    }
    // Tier check — reject platform-tier grants when the caller is a tenant
    // admin. Bootstrap callers pass `tier: 'platform'` to bypass this.
    if (tier === 'tenant' && def.tier === 'platform') {
      errors.push({
        path: `permissions.${i}.permission`,
        code: 'INVALID_ENUM_VALUE',
        message: 'this permission is platform-only and cannot be granted from a tenant role',
      });
      return;
    }
    if (seen.has(permission)) {
      errors.push({
        path: `permissions.${i}.permission`,
        code: 'UNIQUE_VIOLATION',
        message: 'duplicate',
      });
      return;
    }
    seen.add(permission);

    const scopeRaw = e.scope;
    if (def.scopes.length === 0) {
      if (scopeRaw !== undefined && scopeRaw !== null) {
        errors.push({
          path: `permissions.${i}.scope`,
          code: 'INVALID_ENUM_VALUE',
          message: 'must be omitted (this permission has no scopes)',
        });
        return;
      }
      value.push({ permission });
      return;
    }

    if (scopeRaw === undefined || scopeRaw === null) {
      errors.push({
        path: `permissions.${i}.scope`,
        code: 'REQUIRED',
        message: `required; must be one of: ${def.scopes.join(', ')}`,
      });
      return;
    }
    if (typeof scopeRaw !== 'string' || !def.scopes.includes(scopeRaw as PermissionScope)) {
      errors.push({
        path: `permissions.${i}.scope`,
        code: 'INVALID_ENUM_VALUE',
        message: `must be one of: ${def.scopes.join(', ')}`,
      });
      return;
    }
    value.push({ permission, scope: scopeRaw as PermissionScope });
  });

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, value };
}

// ---------------------------------------------------------------------------
// Pure state-transition helpers — shared by the UI editor and other callers
// that mutate permissions arrays. Extracted so they can be unit-tested
// without rendering React.
// ---------------------------------------------------------------------------

/**
 * Toggle a permission on/off. When turning on a scoped permission, default
 * the scope to the broadest available (`all`, or the first declared scope).
 */
export function togglePermission(
  value: readonly PermissionEntry[],
  id: string,
  registry: PermissionRegistry = PERMISSION_REGISTRY,
): PermissionEntry[] {
  const has = value.some((e) => e.permission === id);
  if (has) return value.filter((e) => e.permission !== id);
  const def = lookupPermission(id, registry);
  if (!def) return [...value]; // unknown id is a no-op; caller surfaces validation errors
  const fallbackScope = def.scopes[0];
  const entry: PermissionEntry =
    def.scopes.length > 0 && fallbackScope
      ? { permission: id, scope: def.scopes.includes('all') ? 'all' : fallbackScope }
      : { permission: id };
  return [...value, entry];
}

/** Set the scope on an enabled entry. No-op if the entry isn't present. */
export function setPermissionScope(
  value: readonly PermissionEntry[],
  id: string,
  scope: PermissionScope,
): PermissionEntry[] {
  return value.map((e) => (e.permission === id ? { ...e, scope } : e));
}

/**
 * Reconcile a stored permissions array against the registry so it always
 * satisfies {@link validatePermissions}: drop entries for unknown ids and
 * duplicates, strip `scope` from permissions the registry now declares
 * scopeless, and repair a scoped permission whose stored scope is
 * missing/invalid to the broadest allowed scope (`all`, else the first
 * declared scope).
 *
 * Used by the role editor when LOADING a role: a permission that lost its
 * scopes in a registry change (e.g. `customer.sow.view` going presence-only)
 * leaves stale `scope` on existing rows. Without this, the editor round-trips
 * that stale scope unchanged and the save 400s ("must be omitted"). Normalizing
 * on load makes any subsequent save valid regardless of stored staleness — the
 * data migration cleans the rows at rest; this keeps the UI robust meanwhile.
 */
export function normalizePermissions(
  entries: readonly PermissionEntry[],
  registry: PermissionRegistry = PERMISSION_REGISTRY,
): PermissionEntry[] {
  const index = registry === PERMISSION_REGISTRY ? DEFAULT_INDEX : indexRegistry(registry);
  const out: PermissionEntry[] = [];
  const seen = new Set<string>();
  for (const e of entries) {
    const def = index.get(e.permission);
    if (!def || seen.has(e.permission)) continue;
    seen.add(e.permission);
    const fallbackScope = def.scopes.includes('all') ? 'all' : def.scopes[0];
    if (def.scopes.length === 0 || !fallbackScope) {
      out.push({ permission: e.permission });
      continue;
    }
    const scope = e.scope && def.scopes.includes(e.scope) ? e.scope : fallbackScope;
    out.push({ permission: e.permission, scope });
  }
  return out;
}

/**
 * Group registry entries by their `group` for layout. Order within group
 * follows registry order.
 */
export function groupRegistry(
  registry: PermissionRegistry = PERMISSION_REGISTRY,
): Array<[string, PermissionDef[]]> {
  const map = new Map<string, PermissionDef[]>();
  for (const def of registry) {
    const list = map.get(def.group);
    if (list) list.push(def);
    else map.set(def.group, [def]);
  }
  return Array.from(map.entries());
}

/**
 * Diff two permissions arrays. Returns granted/revoked/scope-changed entries
 * so callers (audit narrator, dirty checks) can describe the change.
 */
export interface PermissionsDiff {
  granted: PermissionEntry[];
  revoked: PermissionEntry[];
  scopeChanged: Array<{
    permission: string;
    from: PermissionScope | undefined;
    to: PermissionScope | undefined;
  }>;
}

export function diffPermissions(
  before: readonly PermissionEntry[],
  after: readonly PermissionEntry[],
): PermissionsDiff {
  const b = new Map(before.map((e) => [e.permission, e.scope]));
  const a = new Map(after.map((e) => [e.permission, e.scope]));
  const granted: PermissionEntry[] = [];
  const revoked: PermissionEntry[] = [];
  const scopeChanged: PermissionsDiff['scopeChanged'] = [];
  for (const id of new Set([...b.keys(), ...a.keys()])) {
    const had = b.has(id);
    const has = a.has(id);
    if (has && !had) {
      const scope = a.get(id);
      granted.push(scope ? { permission: id, scope } : { permission: id });
    } else if (had && !has) {
      const scope = b.get(id);
      revoked.push(scope ? { permission: id, scope } : { permission: id });
    } else if (had && has && b.get(id) !== a.get(id)) {
      scopeChanged.push({ permission: id, from: b.get(id), to: a.get(id) });
    }
  }
  return { granted, revoked, scopeChanged };
}
